import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Linkedin, Mail, Phone, Award, Briefcase, GraduationCap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { IMAGES } from '../constants/images';

const Leadership = () => {
  const leaders = [
    {
      name: 'CA Abhinav Kapoor',
      role: 'Finance Vertical Head',
      image: IMAGES.abhinav,
      linkedin: 'https://www.linkedin.com/in/ca-abhinav-kapoor-aborwar-2b083755/',
      qualifications: ['Chartered Accountant (2012)', 'Law Graduate, University of Rajasthan'],
      experience: [
        'Nearly a decade in Finance Sector',
        'Credit Manager at ICICI Bank (3 years)',
        'Expertise in SME Funding & Project Loans'
      ],
      description: 'Abhinav leads our Finance vertical with deep expertise in banking and credit assessment. His experience at ICICI Bank combined with his CA and legal background provides a unique perspective in structuring complex financing solutions for our clients.',
      expertise: ['SME Funding', 'Project Finance', 'Credit Assessment', 'Term Loans']
    },
    {
      name: 'CA Aneesh Patni',
      role: 'Subsidy Vertical Head',
      image: IMAGES.aneesh,
      linkedin: 'https://www.linkedin.com/in/aneesh-patni-88aborwar/',
      qualifications: ['Chartered Accountant (2012)', 'B.Com, University of Rajasthan'],
      experience: [
        'ERP Consultant (3 years)',
        'Expert in State & Central Government Subsidies',
        'Leading all Government Benefits initiatives'
      ],
      description: 'Aneesh heads our Subsidy vertical, successfully navigating both State and Central Government schemes. His background in ERP consulting and finance enables him to provide structured, systematic guidance to enterprises seeking government benefits.',
      expertise: ['Government Subsidies', 'RIPS', 'MSME Schemes', 'Central Schemes']
    },
    {
      name: 'Mr. Jeetesh Dutt Mathur',
      role: 'Marketing Vertical Head',
      image: IMAGES.jeetesh,
      linkedin: 'https://www.linkedin.com/in/jeetesh-mathur-3a6b4441/',
      qualifications: ['B.Com, University of Rajasthan'],
      experience: [
        '15+ years in Retail Finance',
        'Sales Head at Citi Financial (7 years)',
        'AVP at Andromeda (8 years)'
      ],
      description: 'Jeetesh brings over 15 years of retail finance expertise to our team. His extensive network across banks and NBFCs, built during his tenure at Citi Financial and Andromeda, enables us to offer clients the best retail loan options available.',
      expertise: ['Retail Loans', 'Banking Relations', 'Sales Strategy', 'NBFC Partnerships']
    }
  ];

  return (
    <div data-testid="leadership-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="leadership-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-6 shadow-sm">
                Our Leadership
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#0f172a] leading-tight mb-6">
                Meet the Experts
                <span className="gradient-text block">Behind Your Success</span>
              </h1>
              <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                Our leadership team brings together decades of experience in finance, 
                government subsidies, and banking. With their combined expertise, we've 
                helped over 500 clients achieve their financial goals.
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white">
                    <Award size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f172a]">30+ Years</p>
                    <p className="text-sm text-[#64748b]">Combined Experience</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white">
                    <GraduationCap size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f172a]">2 CAs</p>
                    <p className="text-sm text-[#64748b]">Chartered Accountants</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_tofin-redesign/artifacts/ckva3ztb_FBCover.png"
                alt="Our Founders"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Individual Leader Profiles */}
      <section className="py-24" data-testid="leader-profiles">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {leaders.map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-br from-[#3b5998]/20 to-[#00bcd4]/20 rounded-3xl blur-xl"></div>
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="relative rounded-2xl shadow-xl w-full max-w-md mx-auto"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3b5998]/10 to-[#00bcd4]/10 rounded-full mb-4">
                    <Briefcase size={16} className="text-[#3b5998]" />
                    <span className="text-sm font-medium text-[#3b5998]">{leader.role}</span>
                  </div>
                  <h2 className="font-heading text-3xl font-semibold text-[#0f172a] mb-4">
                    {leader.name}
                  </h2>
                  <p className="text-[#64748b] leading-relaxed mb-6">
                    {leader.description}
                  </p>
                  
                  {/* Qualifications */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                      <GraduationCap size={18} className="text-[#00bcd4]" />
                      Qualifications
                    </h4>
                    <ul className="space-y-2">
                      {leader.qualifications.map((qual, i) => (
                        <li key={i} className="text-[#64748b] text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00bcd4]"></span>
                          {qual}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Experience */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                      <Briefcase size={18} className="text-[#00bcd4]" />
                      Experience
                    </h4>
                    <ul className="space-y-2">
                      {leader.experience.map((exp, i) => (
                        <li key={i} className="text-[#64748b] text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#3b5998]"></span>
                          {exp}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Expertise Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {leader.expertise.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 bg-[#f1f5f9] rounded-full text-xs font-medium text-[#64748b]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* LinkedIn Button */}
                  <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`linkedin-${leader.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0077b5] text-white rounded-full text-sm font-medium hover:bg-[#006097] transition-colors"
                  >
                    <Linkedin size={18} />
                    Connect on LinkedIn
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-[#0f172a]" data-testid="leadership-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              Ready to Work with Our Experts?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-8">
              Schedule a consultation and let our experienced team guide you through 
              your financial journey.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="leadership-contact-btn"
                className="gradient-btn px-8 py-4 rounded-full font-medium text-white inline-flex items-center gap-2"
              >
                <Mail size={18} />
                Contact Us
              </Link>
              <a
                href="tel:+919828092250"
                className="px-8 py-4 border-2 border-white/30 text-white rounded-full font-medium hover:bg-white/10 transition-colors inline-flex items-center gap-2"
              >
                <Phone size={18} />
                +91-77910-93252
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Leadership;
